// src/components/profile/SecuritySettings.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Smartphone as SmartphoneIcon,
  Email as EmailIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { updatePassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../firebase';

const SecuritySettings = () => {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  const [sendingVerification, setSendingVerification] = useState(false);

  const handlePasswordChange = (field) => (event) => {
    setPasswords(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (message) setMessage(null);
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setMessage({ type: 'error', text: 'Todos los campos son obligatorios' });
      return;
    }

    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: 'Las contraseñas nuevas no coinciden' });
      return;
    }

    if (passwords.new.length < 6) {
      setMessage({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres' });
      return;
    }

    setUpdating(true);
    setMessage(null);

    try {
      // Nota: Para cambiar la contraseña, primero necesitas re-autenticar al usuario
      // Este es un ejemplo simplificado
      await updatePassword(auth.currentUser, passwords.new);
      
      setPasswords({ current: '', new: '', confirm: '' });
      setMessage({ 
        type: 'success', 
        text: 'Contraseña actualizada correctamente' 
      });
    } catch (error) {
      let errorMessage = 'Error al cambiar la contraseña';
      
      if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña es muy débil';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Por seguridad, debes iniciar sesión nuevamente antes de cambiar tu contraseña';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setUpdating(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    setSendingVerification(true);
    try {
      await sendEmailVerification(auth.currentUser);
      setMessage({ 
        type: 'success', 
        text: 'Email de verificación enviado. Revisa tu bandeja de entrada.' 
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error al enviar el email de verificación' 
      });
    } finally {
      setSendingVerification(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Configuración de Seguridad
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Gestiona la seguridad de tu cuenta
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      {/* Estado de verificación de email */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <EmailIcon color={user?.emailVerified ? 'success' : 'warning'} sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle1">
                  Verificación de Email
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.emailVerified ? 'Tu email está verificado' : 'Tu email no está verificado'}
                </Typography>
              </Box>
            </Box>
            {!user?.emailVerified && (
              <Button
                variant="outlined"
                onClick={handleSendVerificationEmail}
                disabled={sendingVerification}
                size="small"
              >
                {sendingVerification ? 'Enviando...' : 'Verificar'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Cambiar contraseña */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Cambiar Contraseña
      </Typography>

      <Box component="form" onSubmit={handleChangePassword} sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contraseña actual"
              type={showPassword.current ? 'text' : 'password'}
              value={passwords.current}
              onChange={handlePasswordChange('current')}
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <IconButton
                    onClick={() => togglePasswordVisibility('current')}
                    edge="end"
                  >
                    {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nueva contraseña"
              type={showPassword.new ? 'text' : 'password'}
              value={passwords.new}
              onChange={handlePasswordChange('new')}
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <IconButton
                    onClick={() => togglePasswordVisibility('new')}
                    edge="end"
                  >
                    {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
              helperText="Mínimo 6 caracteres"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Confirmar nueva contraseña"
              type={showPassword.confirm ? 'text' : 'password'}
              value={passwords.confirm}
              onChange={handlePasswordChange('confirm')}
              error={passwords.confirm && passwords.new !== passwords.confirm}
              helperText={
                passwords.confirm && passwords.new !== passwords.confirm
                  ? "Las contraseñas no coinciden"
                  : ""
              }
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <IconButton
                    onClick={() => togglePasswordVisibility('confirm')}
                    edge="end"
                  >
                    {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              disabled={updating}
              startIcon={<SecurityIcon />}
            >
              {updating ? 'Actualizando...' : 'Cambiar Contraseña'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Configuraciones de seguridad adicionales */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Configuraciones Adicionales
      </Typography>

      <List>
        <ListItem>
          <ListItemIcon>
            <SmartphoneIcon />
          </ListItemIcon>
          <ListItemText
            primary="Autenticación de dos factores"
            secondary="Próximamente disponible"
          />
          <ListItemSecondaryAction>
            <Switch disabled />
          </ListItemSecondaryAction>
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <ShieldIcon />
          </ListItemIcon>
          <ListItemText
            primary="Notificaciones de seguridad"
            secondary="Recibir alertas sobre actividad sospechosa"
          />
          <ListItemSecondaryAction>
            <Switch defaultChecked />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
    </Box>
  );
};

export default SecuritySettings;